import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  TextField,
  Stack,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  ArrowUpward,
  ArrowDownward,
  Info as InfoIcon,
} from '@mui/icons-material';
import EmployeeService from '../../services/employee.service';

const EmployeeList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [positionFilter, setPositionFilter] = useState<string>('');
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await EmployeeService.getAll({
        populate: 'department',
        'pagination[pageSize]': 100,
      });
      setEmployees(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await EmployeeService.delete(id);
        fetchEmployees();
      } catch (error) {
        console.error('Error deleting employee:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'onLeave':
        return 'warning';
      case 'suspended':
        return 'error';
      case 'terminated':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Get unique positions for filter
  const getUniquePositions = () => {
    const positions = new Set<string>();
    employees.forEach((emp) => {
      if (emp.attributes?.position) {
        positions.add(emp.attributes.position);
      }
    });
    return Array.from(positions).sort();
  };

  const filteredEmployees = employees
    .filter(
      (emp) => {
        const matchesSearch =
          emp.attributes?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          emp.attributes?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          emp.attributes?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          emp.attributes?.employeeNumber?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesPosition = !positionFilter || emp.attributes?.position === positionFilter;
        
        return matchesSearch && matchesPosition;
      }
    )
    .sort((a, b) => {
      let aValue, bValue;
      
      if (sortField === 'name') {
        aValue = `${a.attributes?.firstName} ${a.attributes?.lastName}`;
        bValue = `${b.attributes?.firstName} ${b.attributes?.lastName}`;
      } else if (sortField === 'createdAt') {
        aValue = new Date(a.attributes?.createdAt || 0).getTime();
        bValue = new Date(b.attributes?.createdAt || 0).getTime();
      } else {
        aValue = a.attributes?.[sortField] || '';
        bValue = b.attributes?.[sortField] || '';
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  // Mobile Card View
  const MobileEmployeeCard = ({ employee }: { employee: any }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h6" component="div">
                {employee.attributes?.firstName} {employee.attributes?.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                #{employee.attributes?.employeeNumber}
              </Typography>
            </Box>
            <Chip
              label={employee.attributes?.status || 'active'}
              color={getStatusColor(employee.attributes?.status)}
              size="small"
            />
          </Stack>
          
          <Stack spacing={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <EmailIcon fontSize="small" color="action" />
              <Typography variant="body2">{employee.attributes?.email}</Typography>
            </Box>
            
            {employee.attributes?.position && (
              <Box display="flex" alignItems="center" gap={1}>
                <WorkIcon fontSize="small" color="action" />
                <Typography variant="body2">{employee.attributes?.position}</Typography>
              </Box>
            )}
            
            {employee.attributes?.department?.data?.attributes?.name && (
              <Box display="flex" alignItems="center" gap={1}>
                <BusinessIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {employee.attributes?.department?.data?.attributes?.name}
                </Typography>
              </Box>
            )}
          </Stack>
        </Stack>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
        <IconButton
          size="small"
          onClick={() => navigate(`/employees/${employee.id}`)}
          color="primary"
        >
          <ViewIcon />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => navigate(`/employees/${employee.id}/edit`)}
          color="primary"
        >
          <EditIcon />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => handleDelete(employee.id)}
          color="error"
        >
          <DeleteIcon />
        </IconButton>
      </CardActions>
    </Card>
  );

  return (
    <Box>
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        justifyContent="space-between" 
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={{ xs: 2, sm: 0 }}
        mb={3}
      >
        <Typography variant="h4">Employees</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/employees/create')}
          fullWidth={isMobile}
        >
          Add Employee
        </Button>
      </Stack>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search employees..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: '200px' }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Position</InputLabel>
          <Select
            value={positionFilter}
            label="Filter by Position"
            onChange={(e) => setPositionFilter(e.target.value)}
          >
            <MenuItem value="">All Positions</MenuItem>
            {getUniquePositions().map((position) => (
              <MenuItem key={position} value={position}>
                {position}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleSort('name')}
            startIcon={sortField === 'name' && (sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
          >
            Name
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleSort('employeeNumber')}
            startIcon={sortField === 'employeeNumber' && (sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
          >
            ID
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleSort('status')}
            startIcon={sortField === 'status' && (sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
          >
            Status
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleSort('createdAt')}
            startIcon={sortField === 'createdAt' && (sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
          >
            Date
          </Button>
        </Box>
      </Box>

      {isMobile ? (
        // Mobile Card View
        <Box>
          {loading ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography>Loading...</Typography>
            </Paper>
          ) : filteredEmployees.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography>No employees found</Typography>
            </Paper>
          ) : (
            filteredEmployees.map((employee) => (
              <MobileEmployeeCard key={employee.id} employee={employee} />
            ))
          )}
        </Box>
      ) : (
        // Desktop Table View
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee #</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Position</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredEmployees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No employees found
                  </TableCell>
                </TableRow>
              ) : (
                filteredEmployees.map((employee) => (
                  <TableRow key={employee.id}>
                    <TableCell>{employee.attributes?.employeeNumber}</TableCell>
                    <TableCell>
                      {employee.attributes?.firstName} {employee.attributes?.lastName}
                    </TableCell>
                    <TableCell>{employee.attributes?.email}</TableCell>
                    <TableCell>{employee.attributes?.position || '-'}</TableCell>
                    <TableCell>
                      {employee.attributes?.department?.data?.attributes?.name || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={employee.attributes?.status || 'active'}
                        color={getStatusColor(employee.attributes?.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/employees/${employee.id}`)}
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/employees/${employee.id}/edit`)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(employee.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default EmployeeList;

